import { isEVMChain } from '@particle-network/connector-core';
import { useRef, useState } from 'react';
import { RoutePath } from '..';
import EmailIcon from '../../assets/icons/EmailIcon';
import PhoneIcon from '../../assets/icons/PhoneIcon';
import CircleSpinner from '../../components/circleSpinner';
import CodeInput from '../../components/codeInput';
import { PageContent } from '../../components/modal/styles';
import ReSend from '../../components/reSend';
import { useContext } from '../../context';
import { useAppearance } from '../../hooks/useAppearance';
import useConnect from '../../hooks/useConnect';
import { useConnector } from '../../hooks/useConnectors';
import useLocales from '../../hooks/useLocales';
import { useModalError } from '../../hooks/useModalError';
import { useRouter } from '../../hooks/useRouter';
import { useWalletConnector } from '../../hooks/useWalletConnectors';
import { ConnectorType } from '../../types';
import { EmailCaptchaTip1, EmailCaptchaWrapper, EmailIconDiv, EmailInfo, EmailMainIcon, EmailText } from './styles';

type Params =
  | {
      code: string;
      phone: string;
    }
  | {
      code: string;
      email: string;
    };

const Page = ({ value, type }: { value: string; type: ConnectorType }) => {
  const locales = useLocales();
  const { config } = useContext();
  const { error, showError } = useModalError();
  const connector = useConnector(isEVMChain(config.chains[0]) ? 'particleEVM' : 'particleSolana');
  const { connectAsync } = useConnect();
  const { appearance } = useAppearance();

  const [loading, setLoading] = useState(false);

  const emailCaptchaWrapperRef = useRef<HTMLDivElement>(null);

  const walletConnector = useWalletConnector('particleAuth');

  const { navigate } = useRouter();

  const loginHandler = async (params: Params) => {
    await connectAsync({ connector, authParams: params });
  };

  const onCodeInputChange = async (code) => {
    try {
      if (loading) return;

      if (code.length === 6) {
        if (/![^0-9]/.test(code)) {
          throw new Error(locales.incorrectCode);
        } else {
          setLoading(true);
          // 请求接口开始登录
          const params = {
            code,
            ...(type === ConnectorType.Phone ? { phone: value } : { email: value }),
          };
          if (!params.code) {
            throw new Error('Please enter the verification code');
            // @ts-ignore
          } else if (!params.phone && !params.email) {
            throw new Error('Please enter the phone number or email');
          }
          // 验证码登录
          await loginHandler(params);
        }
      } else {
        showError('');
      }
    } catch (error: any) {
      let message = error?.extra?.[0] || error.message || (typeof error === 'string' ? error : locales.networkError);
      if (message.includes('Invalid code')) {
        message = locales.invalidCode;
      } else if (message.includes('Code is deleted')) {
        message = locales.codeDeleted;
      } else if (message.includes('Network Error')) {
        message = locales.networkError;
      }
      if (error.code === 4011) {
        navigate(RoutePath.Signup);
        setTimeout(() => showError(message));
      } else {
        showError(message);
      }
    }
    setLoading(false);
  };

  const reSendCode = async () => {
    let turnstileToken = '';
    if (type === ConnectorType.Phone) {
      turnstileToken = await walletConnector?.auth.getCloudflareTurnstileResponse({
        // @ts-ignore
        theme: appearance.mode || 'lgiht',
        language: appearance.language || 'en',
        getContainer: emailCaptchaWrapperRef.current as HTMLElement,
      });
    }
    const params =
      type === ConnectorType.Phone
        ? {
            phone: value,
            // eslint-disable-next-line camelcase
            cf_turnstile_response: turnstileToken,
          }
        : { email: value };

    await walletConnector?.auth.getConnectCaptcha(params);
  };

  return (
    <PageContent
      style={{
        padding: '12px 2px 8px',
      }}
    >
      <EmailCaptchaWrapper ref={emailCaptchaWrapperRef}>
        <EmailMainIcon>
          {/* <EmailIconDiv>{type === 'phone' ? <PhoneIcon /> : <EmailIcon />}</EmailIconDiv> */}
          {/* <Spin>
            <LoadingIcon />
          </Spin> */}
          <CircleSpinner
            width={70}
            height={70}
            inset={2}
            logo={<EmailIconDiv>{type === 'phone' ? <PhoneIcon /> : <EmailIcon />}</EmailIconDiv>}
          />
        </EmailMainIcon>
        <EmailInfo>
          <EmailCaptchaTip1>{locales.verificationCode}</EmailCaptchaTip1>
          <EmailText>{value}</EmailText>
        </EmailInfo>
        <CodeInput
          allowedCharacters='numeric'
          length={6}
          placeholder={' '}
          onChange={onCodeInputChange}
          error={error || ''}
        />
        <ReSend reSendCode={reSendCode} />
      </EmailCaptchaWrapper>
    </PageContent>
  );
};

export default Page;
